import React, { CSSProperties, FC } from "react";
import Card from 'evcharging/components/Card';
import Box from 'evcharging/components/Box';
import Title from 'evcharging/components/Title';
import Description from 'evcharging/components/Description';
import Spacer from 'evcharging/components/Spacer';
import { CONTACT_US_ID } from 'evcharging/app/seo/urls';
import useTheme from 'evcharging/hooks/useTheme';
import { css } from '@emotion/css';
import Section from "evcharging/containers/layout/Section";
import useInput from "evcharging/hooks/useInput";
import {oneWord, trim} from "evcharging/app/validation/filters";
import useI18n from "evcharging/hooks/useI18n";
import useFormResponse from "evcharging/hooks/useFormResponse";
import ResponsePopup from "evcharging/components/ResponsePopup";
import Input from "evcharging/components/Input";
import InputError from "evcharging/components/InputError";
import Responsible from "evcharging/components/Responsible";
import Textarea from "evcharging/components/Textarea";
import Button from "evcharging/components/Button";
import Loader from "evcharging/components/Loader";
import Form from "evcharging/components/Form";
import sendServiceContactForm from "../../evcharging/app/client/services/sendServiceContactForm";
import { E5Service } from 'evcharging/services/types';
import { RetailGreenCheckbox } from "../retail/structures";

interface RequestConsultationCardProps extends CSSProperties {
  service: E5Service;
}

const RequestConsultationCard: FC<RequestConsultationCardProps> = (props) => {
  const { service } = props;
  const theme = useTheme();

  const name = useInput([trim]);
  const phone = useInput([trim]);
  const email = useInput([oneWord]);
  const comments = useInput([trim]);
  const agreeToReceive = useInput([trim], 'no');
  const { t } = useI18n();

  const { isLoading, onSubmit, ...popup } = useFormResponse(
    { name, email, phone, comments, agreeToReceive },
    async (values: Record<string, string>) => {
      await sendServiceContactForm({...values, service: service.id, serviceName: service.name, formImg: service.contact_form_img });
    },
  );

  return (
    <Section backgroundColor="#F6F8F9">
      <Card maxWidth={810} backgroundColor={'white'}>
        <Box marginBottom={32}>
          <Title variant={'p'} fontSize={24} id={CONTACT_US_ID}>
            {service.lead_offer}
          </Title>
          <Spacer value={12} />
          <Description fontSize={16}>
            <Box
              maxWidth={450}
              className={css({
                [theme.breakpoints.mobile.down]: {
                  margin: '0 auto',
                },
              })}
            >
              Please leave your contact details and our Customer Service will get back to you within one business day.
            </Box>
          </Description>
        </Box>

        <Form onSubmit={onSubmit}>
          <ResponsePopup {...popup} />
          <Input
            required
            label={t('form.lead.name')}
            placeholder={t('form.lead.name')}
            name={'name'}
            onChangeValue={name.onChangeValue}
            value={name.value}
            onBlur={() => {
              name.setError('');
            }}
            invalid={!!name.error}
          >
            <InputError message={name.error} />
          </Input>
          <Responsible fractions={[48, 48]} space={0}>
            <Input
              required
              label={t('form.lead.email')}
              placeholder={t('form.lead.email')}
              name={'email'}
              onChangeValue={email.onChangeValue}
              value={email.value}
              onBlur={() => {
                email.setError('');
              }}
              invalid={!!email.error}
            >
              <InputError message={email.error} />
            </Input>
            <Input
              label={t('form.lead.phone')}
              type={'number'}
              placeholder={t('form.lead.phone')}
              name={'phone'}
              onChangeValue={phone.onChangeValue}
              onBlur={() => {
                phone.setError('');
              }}
              value={phone.value}
              invalid={!!phone.error}
            >
              <InputError message={phone.error} />
            </Input>
          </Responsible>
          <Textarea
            placeholder={t('form.lead.type')}
            label={t('form.lead.comments')}
            name={'comments'}
            onChangeValue={comments.onChangeValue}
            onBlur={() => {
              comments.setError('');
            }}
            value={comments.value}
          />
          <RetailGreenCheckbox
            label={t('form.label.agree-receive')}
            onChange={(event) => {
              agreeToReceive.onChangeValue(event.target.checked ? 'yes' : 'no');
            }}
            checked={agreeToReceive.value === 'yes'}
          />
          <Spacer value={32} />
          <Button type="submit" disabled={isLoading} fullWidth>
            {isLoading ? <Loader /> : t('form.lead.button')}
          </Button>
        </Form>
      </Card>
    </Section>
  );
};

export default RequestConsultationCard;
